import { isRiskyEmailPolicy, RiskyEmailPolicy } from "@signalist/shared-types";

import { ListEmailDeliverabilityOverrides } from "../../../schema/list-email-deliverability.schema";

/**
 * A list's two email-deliverability overrides as List Settings edits them (PREC-4261). `null` is
 * "use the workspace default" and stays distinct from an explicit `false`.
 */
export interface EmailOverrides {
	emailVerificationEnabled: boolean | null;
	riskyEmailPolicy: RiskyEmailPolicy | null;
}

/** The dropdown id of the "Workspace default (currently: X)" option. */
export const INHERIT_OPTION_ID = "INHERIT";
export const VERIFICATION_ON_ID = "ON";
export const VERIFICATION_OFF_ID = "OFF";

/** The stored overrides; a value the API did not send, or one it does not know, inherits. */
export const storedEmailOverridesOf = (
	list: ListEmailDeliverabilityOverrides | null | undefined
): EmailOverrides => ({
	emailVerificationEnabled:
		typeof list?.emailVerificationEnabled === "boolean" ? list.emailVerificationEnabled : null,
	riskyEmailPolicy: isRiskyEmailPolicy(list?.riskyEmailPolicy) ? list.riskyEmailPolicy : null,
});

/**
 * Only the overrides the user changed. The settings save sends these and nothing else, so saving
 * any other list setting never writes the two columns (and never turns an explicit value back into
 * NULL, or NULL into a value).
 */
export const changedEmailOverrides = (
	stored: EmailOverrides,
	current: EmailOverrides
): Partial<EmailOverrides> => ({
	...(stored.emailVerificationEnabled !== current.emailVerificationEnabled && {
		emailVerificationEnabled: current.emailVerificationEnabled,
	}),
	...(stored.riskyEmailPolicy !== current.riskyEmailPolicy && {
		riskyEmailPolicy: current.riskyEmailPolicy,
	}),
});

/** The verification dropdown's id for a stored value; `null` (inherit) has no explicit id. */
export const verificationOptionIdOf = (value: boolean | null): string | null =>
	value === null ? null : value ? VERIFICATION_ON_ID : VERIFICATION_OFF_ID;

/** The value a verification dropdown id stands for; `null` means "use the workspace default". */
export const verificationValueOf = (optionId: string | null): boolean | null =>
	optionId === null ? null : optionId === VERIFICATION_ON_ID;

/** The policy a risky-email dropdown id stands for; `null` means "use the workspace default". */
export const riskyPolicyValueOf = (optionId: string | null): RiskyEmailPolicy | null =>
	isRiskyEmailPolicy(optionId) ? optionId : null;
